import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { IconComponent } from '../icon/icon.component';
import { StatusBadgeComponent } from '../status/status-badge.component';

/** Parole che non danno iniziali: articoli e preposizioni, anche articolate o elise. */
const MINOR_WORDS = new Set([
  'a',
  'al',
  'alla',
  'alle',
  'agli',
  'ai',
  'con',
  'da',
  'dal',
  'dalla',
  'dei',
  'del',
  'della',
  'delle',
  'degli',
  'di',
  'e',
  'ed',
  'il',
  'in',
  'la',
  'le',
  'lo',
  'gli',
  'i',
  'nel',
  'nella',
  'per',
  'su',
  'sul',
  'sulla',
  'tra',
  'fra',
]);

/**
 * Iniziali del segnaposto: prima lettera della prima e dell'ultima parola significativa
 * ("Residenza Via dei Tigli" → "RT", "Ponte sull'Adda" → "PA"). Una sola parola dà una lettera.
 */
export function siteInitials(name: string): string {
  const words = name
    .split(/[\s'’-]+/)
    .map((word) => word.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter((word) => word && !MINOR_WORDS.has(word.toLowerCase()));
  if (words.length === 0) return '';
  const first = words[0][0];
  const last = words.length > 1 ? words[words.length - 1][0] : '';
  return (first + last).toLocaleUpperCase('it');
}

/** Tronca il testo della targa, che in SVG non va a capo né si accorcia da solo. */
function clip(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

/**
 * Scheda del cantiere (FR-010, tavola 1A): immagine o segnaposto (la targa P14 del canvas di
 * design), stato e "Appalto pubblico" sull'immagine, nome, descrizione, posizione, account se
 * diverso dal proprio, ruoli professionali in giallo chiaro e ruolo funzionale in grigio. Tutta la
 * scheda è un collegamento alla Panoramica; i nomi lunghi si troncano e il nome completo resta nel
 * `title` e nel nome accessibile.
 */
@Component({
  selector: 'bs-site-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, IconComponent, StatusBadgeComponent],
  host: { class: 'block' },
  template: `
    <a
      [routerLink]="link()"
      data-testid="site-card"
      [attr.data-site-id]="siteId()"
      class="group flex h-full flex-col overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm transition-colors hover:border-stone-300 dark:border-stone-800 dark:bg-stone-900 dark:hover:border-stone-600"
    >
      <div class="relative aspect-video w-full overflow-hidden bg-stone-100 dark:bg-stone-800">
        <div class="absolute left-3 top-3 z-10 flex flex-wrap gap-1.5">
          <bs-status-badge [status]="status()" />
          @if (suspended()) {
            <span
              [class]="pillClass + ' bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300'"
              data-testid="site-card-suspended"
            >
              <span class="size-1.5 rounded-full bg-current" aria-hidden="true"></span>
              Partecipazione sospesa
            </span>
          }
          @if (readOnly()) {
            <bs-status-badge status="read_only" data-testid="site-card-read-only" />
          }
          @if (publicProcurement()) {
            <span
              [class]="pillClass + ' bg-white text-ink dark:bg-stone-900 dark:text-stone-50'"
              data-testid="site-card-public"
            >
              Appalto pubblico
            </span>
          }
        </div>
        @if (imageUrl() && !failed()) {
          <img
            [src]="imageUrl()"
            alt=""
            class="size-full object-cover"
            loading="lazy"
            (error)="failed.set(true)"
          />
        } @else {
          <!-- Segnaposto P14 "Targa e sotto-targa": iniziali sul giallo, nome e comune nella fascia. -->
          <svg
            viewBox="0 0 420 236"
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
            data-placeholder="true"
            class="block size-full"
            [class.grayscale]="status() === 'archived'"
            [class.opacity-75]="status() === 'archived'"
          >
            <rect width="420" height="236" fill="#F5C400" />
            <text
              x="26"
              y="148"
              font-family="'Barlow Condensed', sans-serif"
              font-weight="800"
              font-size="150"
              fill="#111111"
              data-testid="site-placeholder-initials"
            >
              {{ initials() }}
            </text>
            <rect x="0" y="170" width="420" height="66" fill="#111111" />
            <text
              x="26"
              y="204"
              font-family="'Barlow Condensed', sans-serif"
              font-weight="800"
              font-size="24"
              fill="#FFFFFF"
            >
              {{ plateName() }}
            </text>
            <text
              x="26"
              y="224"
              font-family="'JetBrains Mono', monospace"
              font-size="11"
              fill="#F5C400"
            >
              {{ plateLocation() }}
            </text>
            <path d="M372 236 L420 188 L420 236 Z" fill="#F5C400" />
          </svg>
        }
      </div>
      <div class="flex flex-1 flex-col gap-2 p-5">
        <h3
          class="min-w-0 truncate text-base font-semibold text-ink dark:text-stone-50"
          [attr.title]="name()"
        >
          {{ name() }}
        </h3>
        @if (description(); as text) {
          <p class="line-clamp-2 text-sm text-stone-600 dark:text-stone-300">{{ text }}</p>
        }
        <div class="mt-auto flex flex-col gap-1 pt-2 text-sm text-stone-600 dark:text-stone-300">
          <span class="flex items-center gap-2">
            <bs-icon name="map-pin" [size]="16" />
            <span class="truncate">{{ location() }}</span>
          </span>
          @if (accountName(); as account) {
            <span class="flex items-center gap-2" data-testid="site-card-account">
              <bs-icon name="building" [size]="16" />
              <span class="truncate" [attr.title]="account">{{ account }}</span>
            </span>
          }
        </div>
        @if (professionalRoles().length > 0 || permission()) {
          <p class="flex flex-wrap gap-1.5 pt-1" data-testid="site-card-roles">
            <span class="sr-only">Il tuo ruolo:</span>
            @for (item of professionalRoles(); track item) {
              <span
                [class]="pillClass + ' bg-bsite-100 text-ink dark:bg-bsite-950 dark:text-bsite-100'"
                data-testid="site-card-professional-role"
                >{{ item }}</span
              >
            }
            @if (permission(); as functional) {
              <span
                [class]="
                  pillClass +
                  ' bg-stone-100 font-normal text-stone-600 dark:bg-stone-800 dark:text-stone-300'
                "
                data-testid="site-card-role"
                >{{ functional }}</span
              >
            }
          </p>
        }
      </div>
    </a>
  `,
})
export class SiteCardComponent {
  readonly siteId = input<string | null>(null);
  readonly name = input.required<string>();
  readonly description = input<string | null>(null);
  readonly location = input.required<string>();
  readonly status = input<'active' | 'archived'>('active');
  readonly accountName = input<string | null>(null);
  readonly imageUrl = input<string | null>(null);
  readonly link = input.required<string | readonly unknown[]>();
  /** Etichette dei ruoli professionali dell'utente nel cantiere. */
  readonly professionalRoles = input<readonly string[]>([]);
  /** Ruolo funzionale in parole: Amministratore, Partecipante o Visualizzatore. */
  readonly permission = input<string | null>(null);
  readonly publicProcurement = input(false);
  /** La partecipazione dell'utente è sospesa: vede il cantiere senza contenuti. */
  readonly suspended = input(false);
  /** Oltre il limite del piano: consultabile in sola lettura (FR-031). */
  readonly readOnly = input(false);

  protected readonly pillClass =
    'inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-xs font-semibold';

  protected readonly failed = signal(false);
  protected readonly initials = computed(() => siteInitials(this.name()));
  protected readonly plateName = computed(() => clip(this.name().toLocaleUpperCase('it'), 30));
  protected readonly plateLocation = computed(() => clip(this.location(), 44));
}
